/* eslint-disable no-lonely-if */
import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useLocation, useParams } from 'react-router';
import { isSameDay, parseISO } from 'date-fns';
import { LessonComponent } from './lesson-component';
import { DateTime, ILesson, ILessonErrors, IOfferPeriod } from '../../interfaces';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { DatePickerValue } from '../../../../components/date-picker';
import { TimePickerValue } from '../../../../components/time-picker';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import {
  AttendanceSheetStateDto,
  CitizenLessonAttendanceModel,
  ClassroomDto,
  CourseDto,
  CreateLessonDto,
  EducatorDto,
  LessonDto,
  LessonStateDto,
  OccupiedDate,
} from '../../../../api-service/clients';
import { combineDateAndTime, toDate } from '../../../../utilities';
import { validateLesson } from '../../validation';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { getDateFromTimeAndMinutes, getOverlappingDates, isClosedDay, REFRESH_COURSE_LESSON_TABLE, TimeObject, toSchultzDate } from '../../utils';
import { calculateDates } from '../../rrule-utils';
import { LessonWarningConfirmDialog } from '../components/lesson-warning-confirm-dialog';
import { CancelLessonDialog } from '../components/cancel-lesson-dialog';
import { useMetaTitle } from '../../../../hooks';
import { isNullOrUndefined } from '../../../../utilities/null-undefined-helper';
import { DeleteLessonDialog } from '../components/delete-lesson-dialog';
import { useAttendanceRights } from '../../use-attendance-rights';
import { useFeatureToggles } from '../../../../feature-toggles';

const attendanceClient = moduleClientFactory.createAttendanceClient();
const attendanceProtocolClient = moduleClientFactory.createAttendanceProtocolClient();

export function Lesson() {
  const { lessonId } = useParams();

  const [lesson, setLesson] = useImmer<ILesson>({
    attendanceCheckEnd: false,
    attendanceCheckStart: false,
    courseName: '',
    courseId: '',
    isReoccuringLesson: false,
    weekdays: [],
    lessonState: LessonStateDto.Draft,
    educatorIds: [],
    occupiedSeats: 0,
    attendanceSheetState: AttendanceSheetStateDto.Open,
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [moveConfirmDialogOpen, setMoveConfirmDialogOpen] = useState(false);
  const [cancelLessonDialogOpen, setCancelLessonDialogOpen] = useState(false);
  const [deleteLessonDialogOpen, setDeleteLessonDialogOpen] = useState(false);

  const [errors, setErrors] = useState<ILessonErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [occupiedDates, setOccupiedDates] = useState<OccupiedDate[]>([]);
  const [centerClosedDaysMissing, setCenterClosedDaysMissing] = useState(false);
  const [loadedBeginAt, setLoadedBeginAt] = useState<Date>();
  const [overlappingDates, setOverlappingDates] = useState<DateTime[]>([]);
  const [reoccuringDates, setReoccuringDates] = useState<Date[]>([]);
  const [classRooms, setClassRooms] = useState<ClassroomDto[]>([]);
  const [educators, setEducators] = useState<EducatorDto[]>([]);
  const [citizensOnLesson, setCitizensOnLesson] = useState<CitizenLessonAttendanceModel[]>([]);
  const [restrictedCitizenCount, setRestrictedCitizenCount] = useState(0);

  useMetaTitle(lessonId ? 'Redigér lektion' : 'Opret lektion');

  const { search } = useLocation();
  const courseId = new URLSearchParams(search.toLocaleLowerCase()).get('courseid') || undefined;

  const [course, setCourse] = useState<CourseDto>();
  const [offerPeriod, setOfferPeriod] = useState<IOfferPeriod>();

  const postUpdate = usePostToTab(REFRESH_COURSE_LESSON_TABLE);
  const { canSeeProtocol, canEditLesson } = useAttendanceRights();
  const { isFeatureEnabled } = useFeatureToggles();
  const skipClosedDaysEnabled = isFeatureEnabled('EnableSkipHolidaysOnReoccurringLessons');
  const centerClosedDaysEnabled = isFeatureEnabled('EnableSubJobcenterClosedDays');

  useEffect(() => {
    async function loadData() {
      let loadedCourse: CourseDto | undefined;
      if (courseId) {
        const courseResult = await attendanceClient.getCourseById({ courseId });
        loadedCourse = courseResult.course;
        if (courseResult.course?.companyId) {
          const classRoomResult = await attendanceClient.getClassRoomsByCompanyId({ companyId: courseResult.course?.companyId });
          setClassRooms(classRoomResult.classRooms || []);
          const educatorResult = await attendanceClient.getEducatorsByCompanyId({ companyId: courseResult.course?.companyId });
          setEducators((educatorResult.educators || []).filter(x => courseResult.course?.companyId === x.companyId));
        }
        const attendanceCheckStart = !isNullOrUndefined(courseResult.course?.earliestArrivalOnTimeBeforeStart);
        const attendanceCheckEnd = !isNullOrUndefined(courseResult.course?.earliestDepartureOnTimeBeforeEnd);
        setLesson({
          ...lesson,
          courseName: courseResult.course?.name || '',
          courseId: courseResult.course?.courseId || '',
          classroomId: courseResult.course?.classroomId,
          educatorIds: courseResult.course?.educatorIds || [],
          attendanceCheckStart,
          attendanceCheckEnd,
        });
        setCourse(courseResult.course);
        const occupiedDateResult = await attendanceClient.getOccupiedDates({ courseId });
        setOccupiedDates(occupiedDateResult.occupiedDates || []);
      }
      if (lessonId) {
        const lessonResult = await attendanceClient.getLessonById({ lessonId });
        const { lesson: loadedLesson } = lessonResult;

        if (loadedLesson) {
          const { beginAt, endAt, courseId: lessonCourseId, earliestArrivalOnTimeBeforeStart, latestDepartureOnTimeAfterEnd, lessonState: loadedLessonState } = loadedLesson;
          const courseResult = await attendanceClient.getCourseById({ courseId: lessonCourseId });
          loadedCourse = courseResult.course;
          setCourse(courseResult.course);
          if (courseResult.course?.companyId) {
            const classRoomResult = await attendanceClient.getClassRoomsByCompanyId({ companyId: courseResult.course?.companyId });
            setClassRooms(classRoomResult.classRooms || []);
            const educatorResult = await attendanceClient.getEducatorsByCompanyId({ companyId: courseResult.course?.companyId });
            setEducators((educatorResult.educators || []).filter(x => courseResult.course?.companyId === x.companyId));
          }

          const occupiedDateResult = await attendanceClient.getOccupiedDates({ courseId: lessonCourseId });
          setOccupiedDates(occupiedDateResult.occupiedDates || []);

          const start = new Date(beginAt);
          const end = new Date(endAt);
          setLoadedBeginAt(start);
          setLesson({
            ...loadedLesson,
            startDate: start,
            startTime: { hours: start.getHours(), minutes: start.getMinutes() },
            endTime: { hours: end.getHours(), minutes: end.getMinutes() },
            courseName: courseResult.course?.name || '',
            courseId: courseResult.course?.courseId || '',
            attendanceCheckEnd: !!latestDepartureOnTimeAfterEnd,
            attendanceCheckStart: !!earliestArrivalOnTimeBeforeStart,
            isReoccuringLesson: false,
            lessonState: loadedLessonState || LessonStateDto.Draft,
            weekdays: [],
          });
        }
      }
      if (loadedCourse?.offerId) {
        loadOfferPeriod(loadedCourse.offerId);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    async function loadCitizensOnLesson() {
      if (!lessonId || !canSeeProtocol) {
        return;
      }
      try {
        const result = await attendanceProtocolClient.getCitizenLessonAttendancesByLessonId({ lessonId });
        const sortedCitizens = (result.citizenLessonAttendances || []).sort((a, b) => (a.citizenName || '').localeCompare(b.citizenName || ''));
        setCitizensOnLesson(sortedCitizens);
        setRestrictedCitizenCount(result.restrictedCitizenCount);
      } catch (error) {
        console.error(error);
      }
    }
    loadCitizensOnLesson();
  }, []);

  async function loadOfferPeriod(offerId: string) {
    try {
      const offerPeriodResult = await attendanceClient.getOfferPeriodByOfferId({ offerId });
      setOfferPeriod({
        startDate: toDate(offerPeriodResult.startDate),
        endDate: toDate(offerPeriodResult.endDate),
      });
    } catch (error) {
      console.error(error);
    }
  }

  function mapOccupiedDates(dates: OccupiedDate[]): DateTime[] {
    return dates.map(x => ({ start: parseISO(x.startDate.toString()), end: parseISO(x.endDate.toString()), dateType: x.occupationType, name: x.name }));
  }

  async function getOccupiedDatesInPeriod(from: DatePickerValue, to: DatePickerValue): Promise<DateTime[] | undefined> {
    if (!centerClosedDaysEnabled) {
      return mapOccupiedDates(occupiedDates);
    }
    try {
      setIsLoading(true);
      const result = await attendanceClient.getOccupiedDates({ courseId: lesson.courseId, from: toDate(from), to: toDate(to) });
      setCenterClosedDaysMissing(result.centerClosedDaysMissing);
      return mapOccupiedDates(result.occupiedDates || []);
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateClick(value: LessonStateDto) {
    const { isReoccuringLesson, startDate, startTime, endTime, reoccuringLessonStartDate, reoccuringLessonEndDate, weekdays } = lesson;

    const mappedOccupiedDates = isReoccuringLesson
      ? await getOccupiedDatesInPeriod(reoccuringLessonStartDate, reoccuringLessonEndDate)
      : await getOccupiedDatesInPeriod(startDate, startDate);
    if (!mappedOccupiedDates) {
      return;
    }
    const lessonToSave = { ...lesson, lessonState: value };
    setLesson(lessonToSave);

    if (isReoccuringLesson) {
      // Calculate overlappings
      const reoccuringDatesResult = calculateDates(reoccuringLessonStartDate, reoccuringLessonEndDate, weekdays);
      setReoccuringDates(reoccuringDatesResult);
      const mappedReoccuringDates = reoccuringDatesResult.map(
        x => ({ start: combineDateAndTime(x, startTime)!, end: combineDateAndTime(x, endTime)!, dateType: 'lesson' } as DateTime)
      );

      const overlappingDatesResult = getOverlappingDates(mappedOccupiedDates, mappedReoccuringDates);
      setOverlappingDates(overlappingDatesResult);

      setConfirmDialogOpen(true);
    } else {
      const beginAt = combineDateAndTime(startDate, startTime)!;
      const endAt = combineDateAndTime(startDate, endTime)!;

      const overlappingDatesResult = getOverlappingDates(mappedOccupiedDates, [{ start: beginAt, end: endAt, dateType: 'lesson' }]);
      setOverlappingDates(overlappingDatesResult);
      setReoccuringDates([]);

      if (overlappingDatesResult.length > 0) {
        // Show warning dialog
        setConfirmDialogOpen(true);
      } else {
        createLesson(lessonToSave, true);
      }
    }
  }

  function handleCreateReoccuringDialogConfirmClick(skipClosedDays: boolean) {
    createLesson(lesson, skipClosedDays);
  }

  async function createLesson(lessonToSave: ILesson, skipClosedDays: boolean) {
    try {
      setIsLoading(true);
      await handleSaveLesson(lessonToSave, skipClosedDays);
      window.close();
    } catch (error) {
      // Handle errors
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveLesson(lessonToSave: ILesson, skipClosedDays: boolean) {
    const { startDate, startTime, endTime, reoccuringLessonStartDate, reoccuringLessonEndDate, weekdays, isReoccuringLesson } = lessonToSave;
    const beginAt = combineDateAndTime(isReoccuringLesson ? reoccuringLessonStartDate : startDate, startTime);
    const endAt = combineDateAndTime(isReoccuringLesson ? reoccuringLessonStartDate : startDate, endTime);
    if (beginAt && endAt && courseId) {
      const lessonRequest: CreateLessonDto = { ...lessonToSave, beginAt, endAt, courseId, isDeleted: false, isSubmitted: false };
      if (isReoccuringLesson) {
        await attendanceClient.createReoccuringLessons({
          lesson: lessonRequest,
          reoccuringLessonStartDate: reoccuringLessonStartDate!,
          reoccuringLessonEndDate: reoccuringLessonEndDate!,
          weekdays,
          skipClosedDays,
        });
      } else {
        await attendanceClient.createLesson({ lesson: lessonRequest });
      }
    }
    postUpdate(window.opener);
  }

  async function handleUpdateLessonClick() {
    const { startDate, startTime, endTime } = lesson;
    const beginAt = combineDateAndTime(startDate, startTime);
    const endAt = combineDateAndTime(startDate, endTime);
    const dateChanged = !loadedBeginAt || !beginAt || !isSameDay(loadedBeginAt, beginAt);
    const occupiedDatesOnNewDate = skipClosedDaysEnabled && dateChanged ? await getOccupiedDatesInPeriod(startDate, startDate) : [];
    if (!occupiedDatesOnNewDate) {
      return;
    }
    const closedDays = occupiedDatesOnNewDate.filter(isClosedDay);
    const movedOntoClosedDays = beginAt && endAt ? getOverlappingDates(closedDays, [{ start: beginAt, end: endAt, dateType: 'lesson' }]) : [];

    if (movedOntoClosedDays.length > 0) {
      setOverlappingDates(movedOntoClosedDays);
      setReoccuringDates([]);
      setMoveConfirmDialogOpen(true);
    } else {
      updateLesson();
    }
  }

  async function updateLesson() {
    try {
      setIsLoading(true);
      const { startDate, startTime, endTime, courseId: lessonCourseId } = lesson;
      const beginAt = combineDateAndTime(startDate, startTime);
      const endAt = combineDateAndTime(startDate, endTime);
      if (beginAt && endAt && lessonCourseId) {
        const lessonRequest: LessonDto = {
          ...lesson,
          beginAt,
          endAt,
          courseId: lessonCourseId,
          isDeleted: false,
          isSubmitted: false,
          attendanceSheetState: AttendanceSheetStateDto.Open,
          lessonId,
        };
        await attendanceClient.updateLesson({ lesson: lessonRequest });
      }
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      // Handle errors
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCancelLessonClick() {
    setCancelLessonDialogOpen(true);
  }

  async function handlePublishLessonClick() {
    try {
      setIsLoading(true);
      await attendanceClient.publishLesson({ lessonId });
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCancelLessonConfirm(reason: string) {
    try {
      setIsLoading(true);
      if (reason) {
        await attendanceClient.cancelLesson({ lessonId, cancellationReason: reason });
        setCancelLessonDialogOpen(false);
        postUpdate(window.opener);
        window.close();
      }
    } catch (error) {
      // Error handling
    } finally {
      setIsLoading(false);
    }
  }

  function handleValidate() {
    const [isValid, tmpErrors] = validateLesson(lesson, offerPeriod);

    setErrors({ ...tmpErrors });
    return isValid;
  }

  function handleCreateDraftClick() {
    if (handleValidate()) {
      handleCreateClick(LessonStateDto.Draft);
    }
  }

  function handleYesNoChange(name: string, value: YesNo) {
    setLesson((draft: any) => {
      draft[name] = value === YesNo.Yes;
    });
  }

  function handleAttendanceCheckStartChange(name: string, value: YesNo) {
    handleYesNoChange(name, value);
    const { startTime } = lesson;
    if (value === YesNo.Yes && course && startTime) {
      setArrivalStartTimes(course, startTime as TimeObject);
    } else if (value === YesNo.No) {
      setLesson(draft => {
        draft.earliestArrivalOnTimeBeforeStart = undefined;
        draft.latestArrivalOnTimeAfterStart = undefined;
        draft.latestAttendanceRegistrationAfterStart = undefined;
      });
    }
  }

  function handleAttendanceCheckEndChange(name: string, value: YesNo) {
    handleYesNoChange(name, value);
    const { endTime } = lesson;
    if (value === YesNo.Yes && course && endTime) {
      setDepartureEndTimes(course, endTime as TimeObject);
    } else if (value === YesNo.No) {
      setLesson(draft => {
        draft.latestDepartureOnTimeAfterEnd = undefined;
        draft.earliestDepartureOnTimeBeforeEnd = undefined;
        draft.earliestDepartureRegistrationBeforeEnd = undefined;
      });
    }
  }

  function setArrivalStartTimes(courseDto: CourseDto, startTime: TimeObject) {
    const { earliestArrivalOnTimeBeforeStart, latestArrivalOnTimeAfterStart, latestAttendanceRegistrationAfterStart } = courseDto;
    if (
      !isNullOrUndefined(earliestArrivalOnTimeBeforeStart) &&
      !isNullOrUndefined(latestArrivalOnTimeAfterStart) &&
      !isNullOrUndefined(latestAttendanceRegistrationAfterStart) &&
      startTime
    ) {
      const earliestArrivalOnTimeBeforeStartTime = getDateFromTimeAndMinutes(startTime, earliestArrivalOnTimeBeforeStart || 0, true);
      const latestArrivalOnTimeAfterStartTime = getDateFromTimeAndMinutes(startTime, latestArrivalOnTimeAfterStart || 0);
      const latestAttendanceRegistrationAfterStartTime = getDateFromTimeAndMinutes(startTime, latestAttendanceRegistrationAfterStart || 0);

      setLesson(draft => {
        draft.earliestArrivalOnTimeBeforeStart = earliestArrivalOnTimeBeforeStartTime;
        draft.latestArrivalOnTimeAfterStart = latestArrivalOnTimeAfterStartTime;
        draft.latestAttendanceRegistrationAfterStart = latestAttendanceRegistrationAfterStartTime;
      });
    }
  }

  function setDepartureEndTimes(courseDto: CourseDto, endTime: TimeObject) {
    const { earliestDepartureRegistrationBeforeEnd, earliestDepartureOnTimeBeforeEnd, latestDepartureOnTimeAfterEnd } = courseDto;
    if (
      !isNullOrUndefined(earliestDepartureRegistrationBeforeEnd) &&
      !isNullOrUndefined(earliestDepartureOnTimeBeforeEnd) &&
      !isNullOrUndefined(latestDepartureOnTimeAfterEnd) &&
      endTime
    ) {
      const earliestDepartureRegistrationBeforeEndTime = getDateFromTimeAndMinutes(endTime, earliestDepartureRegistrationBeforeEnd || 0, true);
      const earliestDepartureOnTimeBeforeEndTime = getDateFromTimeAndMinutes(endTime, earliestDepartureOnTimeBeforeEnd || 0, true);
      const latestDepartureOnTimeAfterEndTime = getDateFromTimeAndMinutes(endTime, latestDepartureOnTimeAfterEnd || 0);

      setLesson(draft => {
        draft.earliestDepartureOnTimeBeforeEnd = earliestDepartureOnTimeBeforeEndTime;
        draft.latestDepartureOnTimeAfterEnd = latestDepartureOnTimeAfterEndTime;
        draft.earliestDepartureRegistrationBeforeEnd = earliestDepartureRegistrationBeforeEndTime;
      });
    }
  }

  function handleStringChange(name: string, value: string) {
    setLesson((draft: any) => {
      draft[name] = value;
    });
    setErrors({ ...errors, [name]: '' });
  }

  function handleDateChange(name: string, value: DatePickerValue) {
    setLesson((draft: any) => {
      draft[name] = value;
    });
    setErrors({ ...errors, [name]: '' });
  }

  function handleTimeChange(name: 'startTime' | 'endTime', value: TimePickerValue) {
    setLesson((draft: ILesson) => {
      draft[name] = value;
    });

    if (name === 'startTime' && lesson.attendanceCheckStart && course) {
      setArrivalStartTimes(course, value as TimeObject);
    } else if (name === 'endTime' && lesson.attendanceCheckEnd && course) {
      setDepartureEndTimes(course, value as TimeObject);
    }

    setErrors({ ...errors, [name]: '' });
  }

  function handleTimeToDateChange(name: string, value: TimePickerValue) {
    setLesson((draft: any) => {
      if (value === undefined || value === '') {
        draft[name] = value;
        return;
      }
      const { startDate } = lesson;
      const baseDate = toSchultzDate(startDate || new Date());
      const timeObject = value as TimeObject;
      const date = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), timeObject.hours, timeObject.minutes);
      draft[name] = date;
    });
    setErrors({ ...errors, [name]: '' });
  }

  function handleCheckboxChange(name: string, checked: boolean) {
    setLesson(draft => {
      if (checked) {
        draft.weekdays?.push(name);
      } else {
        draft.weekdays = draft.weekdays?.filter(day => day !== name);
      }
    });
  }

  function handleEducatorsChange(value: EducatorDto[]) {
    setLesson(draft => {
      draft.educatorIds = value.map(x => x.educatorId);
    });
  }

  function handleDeleteLesson() {
    setDeleteLessonDialogOpen(true);
  }

  async function handleDeleteLessonConfirm() {
    try {
      setIsLoading(true);
      await attendanceClient.deleteLesson({ lessonId });
      setDeleteLessonDialogOpen(false);
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function clearWeekdaysError() {
    setErrors({
      ...errors,
      weekdays: '',
    });
  }

  return (
    <>
      <LessonComponent
        isEditmode={!!lessonId}
        isLoading={isLoading}
        onCreateLessonClick={handleCreateDraftClick}
        onCreateAndPublishLessonClick={() => handleCreateClick(LessonStateDto.Published)}
        onUpdateLessonClick={handleUpdateLessonClick}
        onValidate={handleValidate}
        lesson={lesson}
        errors={errors}
        onStringChange={handleStringChange}
        onYesNoChange={handleYesNoChange}
        onCheckboxChange={handleCheckboxChange}
        onDateChange={handleDateChange}
        onTimeChange={handleTimeChange}
        onCancelLesson={handleCancelLessonClick}
        onPublishLesson={handlePublishLessonClick}
        onTimeToDateChange={handleTimeToDateChange}
        onAttendanceCheckStartChange={handleAttendanceCheckStartChange}
        onAttendanceCheckEndChange={handleAttendanceCheckEndChange}
        classRooms={classRooms}
        educators={educators}
        citizensOnLesson={citizensOnLesson}
        restrictedCitizenCount={restrictedCitizenCount}
        isCourseCompleted={course?.isCompleted || false}
        onEducatorsChange={handleEducatorsChange}
        onDeleteLesson={handleDeleteLesson}
        clearWeekdaysErrors={clearWeekdaysError}
        canSeeOrEditProtocol={canSeeProtocol}
        hasAttendanceWrite={canEditLesson}
      />
      <LessonWarningConfirmDialog
        isOpen={confirmDialogOpen || moveConfirmDialogOpen}
        onCancelClick={() => {
          setConfirmDialogOpen(false);
          setMoveConfirmDialogOpen(false);
        }}
        onConfirmClick={moveConfirmDialogOpen ? updateLesson : handleCreateReoccuringDialogConfirmClick}
        startDate={lesson.reoccuringLessonStartDate}
        endDate={lesson.reoccuringLessonEndDate}
        overlappingDates={overlappingDates}
        reoccuringDates={reoccuringDates}
        isReoccuringLesson={lesson.isReoccuringLesson}
        isMovingLesson={moveConfirmDialogOpen}
        skipClosedDaysEnabled={skipClosedDaysEnabled}
        centerClosedDaysEnabled={centerClosedDaysEnabled}
        centerClosedDaysMissing={centerClosedDaysMissing}
        isSaving={isLoading}
      />
      <CancelLessonDialog isOpen={cancelLessonDialogOpen} onCancelClick={() => setCancelLessonDialogOpen(false)} onConfirmClick={handleCancelLessonConfirm} />
      <DeleteLessonDialog isOpen={deleteLessonDialogOpen} onCancelClick={() => setDeleteLessonDialogOpen(false)} onConfirmClick={handleDeleteLessonConfirm} />
    </>
  );
}
